import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    Edit,
    SimpleForm,
    SelectInput,
    useRecordContext,
    useNotify,
    useRefresh,
} from 'react-admin';
import {
    Card,
    CardContent,
    Typography,
    Switch,
    FormControlLabel,
    Box,
    Button,
    ButtonGroup,
    Paper,
    Grid,
    Tooltip,
    IconButton,
    Alert,
    Snackbar,
    Chip,
    CircularProgress,
} from '@mui/material';
import {
    PowerSettingsNew as PowerIcon,
    AutoMode as AutoModeIcon,
    Info as InfoIcon,
    ElectricBolt as ElectricBoltIcon,
} from '@mui/icons-material';
import OverrideHistory from './OverrideHistory';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { useApi } from '../hooks/useApi';

const StatusCard = ({ onStatusChange }) => {
    const { t } = useTranslation();
    const [status, setStatus] = React.useState({ workingStatus: 'auto', relaisStatus: 'off' });
    const [error, setError] = React.useState(null);
    const [isWaitingForStatusUpdate, setIsWaitingForStatusUpdate] = React.useState(false);
    const [expectedWorkingStatus, setExpectedWorkingStatus] = React.useState(null);
    const notify = useNotify();
    const { get, put } = useApi();
    const intervalRef = React.useRef(null);
    const timeoutRef = React.useRef(null);

    // Functie om status updates te fetchen
    const fetchStatus = async () => {
        try {
            const response = await get('/revpi/relay-status');
            const newStatus = response.data;
            
            setStatus(prevStatus => {
                // Check of er een statuswijziging heeft plaatsgevonden
                const statusChanged = 
                    newStatus.workingStatus !== prevStatus.workingStatus || 
                    newStatus.relaisStatus !== prevStatus.relaisStatus;
                
                // Check of we de verwachte working status hebben ontvangen
                const expectedStatusReached = expectedWorkingStatus && 
                    newStatus.workingStatus === expectedWorkingStatus;
                
                if (statusChanged || expectedStatusReached) {
                    console.log('Status wijziging gedetecteerd:', { 
                        van: prevStatus, 
                        naar: newStatus,
                        verwachtedStatus: expectedWorkingStatus,
                        statusBereikt: expectedStatusReached
                    });
                    
                    // Stop de waiting state omdat we een update hebben ontvangen
                    setIsWaitingForStatusUpdate(false);
                    setExpectedWorkingStatus(null);
                    
                    // Clear timeout als die er is
                    if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                        timeoutRef.current = null;
                    }
                    
                    return newStatus;
                }
                return prevStatus;
            });
        } catch (error) {
            setError(error instanceof Error ? error.message : 'Onbekende fout');
        }
    };



    // Functie om waiting state te activeren na statuswijziging
    const startWaitingForUpdate = (expectedStatus) => {
        console.log(`Statuswijziging verzonden naar ${expectedStatus} - wacht op bevestiging`);
        setIsWaitingForStatusUpdate(true);
        setExpectedWorkingStatus(expectedStatus);
        
        // Failsafe timeout na 30 seconden
        timeoutRef.current = setTimeout(() => {
            console.log('Timeout: status update niet ontvangen na 30 seconden');
            setIsWaitingForStatusUpdate(false);
            setExpectedWorkingStatus(null);
        }, 30000);
    };

    const handleStatusChange = async (newStatus) => {
        try {
            if (newStatus !== status.workingStatus) {
                await put('/revpi/relay-control', { value: newStatus });
                setStatus(prev => ({ ...prev, workingStatus: newStatus }));
                notify(t('status.updated', { status: newStatus }), { type: 'success' });
                onStatusChange();
                
                // Start waiting state na handmatige statuswijziging
                startWaitingForUpdate(newStatus);
            }
        } catch (error) {
            setError(error instanceof Error ? error.message : t('errors.unknown'));
        }
    };

    // Effect voor het starten van de eerste fetch
    React.useEffect(() => {
        fetchStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Effect die de interval beheert op basis van waiting status
    React.useEffect(() => {
        // Clear bestaande interval
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        if (isWaitingForStatusUpdate) {
            // Waiting mode: elke 2 seconden totdat status is geüpdatet
            console.log('Wacht op status update - checking elke 2 seconden');
            intervalRef.current = setInterval(() => {
                fetchStatus();
            }, 2000);
        } else {
            // Normale mode: elke 30 seconden
            console.log('Normale mode - checking elke 30 seconden');
            intervalRef.current = setInterval(() => {
                fetchStatus();
            }, 30000);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [isWaitingForStatusUpdate]);

    const getStatusColor = (status) => {
        switch (status) {
            case 'on': return 'success.main';
            case 'off': return 'error.main';
            case 'auto': return 'info.main';
            default: return 'text.secondary';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'on': return <PowerIcon />;
            case 'off': return <PowerIcon />;
            case 'auto': return <AutoModeIcon />;
            default: return null;
        }
    };

    return (
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant="h5" gutterBottom>
                            {t('status.current')}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Chip
                                icon={getStatusIcon(status.workingStatus)}
                                label={status.workingStatus === 'on' ? t('status.directOn') : 
                                       status.workingStatus === 'off' ? t('status.directOff') : t('status.auto')}
                                color={status.workingStatus === 'on' ? 'success' : 
                                       status.workingStatus === 'off' ? 'error' : 'info'}
                                sx={{ fontSize: '1.2rem', py: 2 }}
                            />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {t('status.description')}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant="h5" gutterBottom>
                            {t('relay.status')}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Chip
                                icon={<ElectricBoltIcon />}
                                label={status.relaisStatus === 'on' ? t('relay.on') : t('relay.off')}
                                color={status.relaisStatus === 'on' ? 'success' : 'error'}
                                sx={{ fontSize: '1.2rem', py: 2 }}
                            />
                            {isWaitingForStatusUpdate && (
                                <Tooltip title="Wacht op bevestiging van relais status...">
                                    <CircularProgress 
                                        size={20} 
                                        color="primary"
                                        sx={{ ml: 1 }}
                                    />
                                </Tooltip>
                            )}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {t('relay.description')}
                        </Typography>
                    </Box>
                </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
                <Typography variant="h5" gutterBottom>
                    {t('control.direct')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('control.description')}
                </Typography>
                <ButtonGroup variant="contained" sx={{ mt: 2 }}>
                    <Button
                        startIcon={<PowerIcon />}
                        color={status.workingStatus === 'on' ? 'success' : 'primary'}
                        onClick={() => handleStatusChange('on')}
                    >
                        {t('control.directOn')}
                    </Button>
                    <Button
                        startIcon={<PowerIcon />}
                        color={status.workingStatus === 'off' ? 'error' : 'primary'}
                        onClick={() => handleStatusChange('off')}
                    >
                        {t('control.directOff')}
                    </Button>
                    <Button
                        startIcon={<AutoModeIcon />}
                        color={status.workingStatus === 'auto' ? 'info' : 'primary'}
                        onClick={() => handleStatusChange('auto')}
                    >
                        {t('control.backToPlanning')}
                    </Button>
                </ButtonGroup>
            </Box>

            <Box sx={{ mt: 3 }}>
                <Alert severity="info">
                    <Typography variant="body2">
                        {t('info.description')}
                        <ul>
                            <li><strong>{t('info.directControl')}:</strong> {t('info.directControlDesc')}</li>
                            <li><strong>{t('info.planning')}:</strong> {t('info.planningDesc')}
                                <ul>
                                    <li>{t('info.lowEnergyPrices')}</li>
                                    <li>{t('info.lowCO2')}</li>
                                    <li>{t('info.sufficientRenewable')}</li>
                                </ul>
                                {t('info.planningNote')}
                            </li>
                        </ul>
                    </Typography>
                </Alert>
            </Box>

            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

const OverrideSwitch = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const refresh = useRefresh();
    const { put } = useApi();

    const handleChange = async (event) => {
        try {
            await put('/revpi/relay-control', { value: event.target.checked ? 'on' : 'off' });
            notify('Status bijgewerkt', { type: 'success' });
            refresh();
        } catch (error) {
            notify('Fout bij bijwerken status: ' + error.message, { type: 'error' });
        }
    };

    return (
        <FormControlLabel
            control={
                <Switch
                    checked={record.workingStatus === 'on'}
                    onChange={handleChange}
                    color="primary"
                />
            }
            label={record.workingStatus === 'on' ? 'Aan' : 'Uit'}
        />
    );
};

export const OverrideList = () => {
    const [key, setKey] = React.useState(0);

    const handleStatusChange = () => {
        setKey(prev => prev + 1);
    };

    return (
        <>
            <StatusCard onStatusChange={handleStatusChange} />
            <OverrideHistory key={key} title="Geschiedenis van Overrides" />
        </>
    );
};

export const OverrideEdit = () => (
    <Edit>
        <SimpleForm>
            <SelectInput
                source="workingStatus"
                choices={[
                    { id: 'on', name: 'Aan' },
                    { id: 'off', name: 'Uit' },
                    { id: 'auto', name: 'Auto-pilot' },
                ]}
            />
        </SimpleForm>
    </Edit>
);

// Helper functie voor tijdzone conversie
const formatDateTime = (utcDate) => {
    return moment(utcDate).tz('Europe/Amsterdam').format('DD-MM-YYYY HH:mm:ss');
};

export default StatusCard; 